'use client'

import { Link } from '@tanstack/react-router'
import { usePathname } from '@/hooks/usePathname'
import { useState, useEffect } from 'react'
import { Camera, FolderKanban, History, ChartColumn, Loader2 } from 'lucide-react'
import type { User } from '@supabase/supabase-js'
import { ROUTES } from '@/config/routes'
import { useTranslations } from '@/hooks/useTranslations'
interface MobileTabBarProps {
  user: User
  userRole?: string
}

export function MobileTabBar({ user: _user, userRole: _userRole = 'SITE_MANAGER' }: MobileTabBarProps) {
  const t = useTranslations('components.mobileTabBar')
  const pathname = usePathname()
  const [loadingHref, setLoadingHref] = useState<string | null>(null)

  // Clear loading state when navigation completes
  useEffect(() => {
    setLoadingHref(null)
  }, [pathname])

  const isActive = (path: string) => pathname === path

  // Handle navigation with loading state
  const handleNavigation = (href: string) => {
    if (href !== pathname) {
      setLoadingHref(href)
      // Clear loading state after a delay to prevent it staying too long
      setTimeout(() => setLoadingHref(null), 2000)
    }
  }

  // Mobile bottom bar mirrors the desktop nav's primary items, action-first:
  // Foto / KI Analyse, Projekte, Historie, Statistiken. Everything else
  // (Auftragnehmer, Mangelanzeige, Tipps & Hilfe, …) lives in the Navbar menu.
  // NB: ROUTES.DASHBOARD is the *Statistiken* page (chart icon), not a "home".
  const navItems = [
    { href: ROUTES.DETECT, icon: Camera, label: t('detect') },
    { href: ROUTES.PROJECTS, icon: FolderKanban, label: t('projects') },
    { href: ROUTES.HISTORY, icon: History, label: t('history') },
    { href: ROUTES.DASHBOARD, icon: ChartColumn, label: t('dashboard') },
  ]

  return (
    <div className="md:hidden paper-mobile-tab-bar">
      <nav className="paper-mobile-tab-nav">
        {navItems.map((item) => {
          const Icon = item.icon
          const active = isActive(item.href)
          const isLoading = loadingHref === item.href

          return (
            <Link
              key={item.href}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- nav hrefs are dynamic ROUTES strings, not statically known to TanStack Router
              to={item.href as any}
              onClick={() => handleNavigation(item.href)}
              className={`paper-mobile-tab-item ${
                active ? 'paper-mobile-tab-item-active' : ''
              } ${isLoading ? 'opacity-75' : ''}`}
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin paper-mobile-tab-icon" />
              ) : (
                <Icon className={`paper-mobile-tab-icon ${active ? 'paper-mobile-tab-icon-active' : ''}`} />
              )}
              <span className={`paper-mobile-tab-label ${active ? 'paper-mobile-tab-label-active' : ''}`}>
                {item.label}
              </span>
            </Link>
          )
        })}
      </nav>
    </div>
  )
}
